// Searching INSIDE the files attached to the records on this list.
//
// IT SITS ON THE VIEW LINE, beside the persona tabs, and that placement is what let a whole
// paragraph be deleted. The box used to be an 85px bordered panel above the grid carrying the
// caption "Searching the files attached to Documents you can see as Organization · Position:
// Chief Executive" -- the THIRD statement of the same persona on one screen, after the blue
// header and the tab row. Sitting on the tab line, the active tab IS that sentence, twelve pixels
// away, and the placeholder carries the classification half. So the caption now appears only
// inside the results, where it qualifies an answer, rather than permanently on a page where it
// qualified nothing. Ed, 2026-10-22, from a screenshot of the triplication.
//
// IT IS NOT THE SAME AS THE FILTERS BESIDE IT, and the empty state says so. The sidebar narrows by
// a record's metadata -- number, name, description, form answers. This reads the TEXT INSIDE the
// attached files, which nothing else in the product does, and it matches two ways: on MEANING,
// where "how long are records kept" finds "retained for seven years"; and on WORDING, where
// "SOP-00009" finds that exact token. Each hit says which found it.
//
// AND IT SAYS WHEN IT IS RUNNING ON HALF OF ITSELF. Embedding the question needs a model; ranking
// by wording needs nothing but the database. When the model is at capacity -- which on this account
// is every call, the quota being zero -- the search still answers, by wording alone, and says so
// in amber. A degraded answer that looks identical to a healthy one is the failure worth
// engineering against here: the reader would conclude the corpus says nothing, when the truth is
// that nobody looked for the paraphrase. That line is NOT redundant with anything else on screen,
// which is why it moved into the panel rather than being dropped with the caption.
//
// A HIT IS A PASSAGE, NOT A RECORD. It shows the record, the file, the page and the sentence that
// matched, because the useful answer to "which procedure covers this" is the paragraph, and the
// page is what makes it checkable against the document itself.
import React, {useCallback, useRef, useState} from "react";
import {Box, CircularProgress, IconButton, InputAdornment, Popover, TextField, Tooltip,
        Typography} from "@mui/material";
import {SearchRounded, TravelExploreOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";

import {useSessionContext} from "../../util/SessionContext";
import {usePersona} from "../Common/PersonaHeaderInfo";
import {AttachmentHit, SearchMode, searchAttachments} from "../../data/attachmentSearch";
import {Blue3, Grey2, Grey6, Grey17} from "../../theme/styles";

type Props = {
    /** The classification this list shows; the search is scoped to it. */
    classificationCode: string;
    /** What to call it on screen, e.g. "Documents". */
    classificationPlural: string;
};

export default function SearchAttachments({classificationCode, classificationPlural}: Props) {
    const sessionContext = useSessionContext();
    const navigate = useNavigate();
    const persona = usePersona();

    const [query, setQuery] = useState("");
    const [hits, setHits] = useState<AttachmentHit[] | null>(null);
    const [mode, setMode] = useState<SearchMode | null>(null);
    const [note, setNote] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    // THE RESULTS HANG OFF THE INPUT rather than pushing the grid down. anchor is the input's own
    // wrapper, so the panel lines up under it at any width.
    const anchor = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);

    const run = useCallback(async () => {
        const asked = query.trim();
        if (!asked || busy) return;
        setBusy(true);
        setProblem(null);
        setOpen(true);
        try {
            const found = await searchAttachments(sessionContext, asked, classificationCode, 10);
            setHits(found.hits);
            setMode(found.mode);
            setNote(found.note);
        } catch (failure: any) {
            // THE SERVER'S OWN SENTENCE WHERE IT WROTE ONE. Every refusal here is user_facing and
            // says something actionable -- no persona, nothing indexed with this model, the
            // embedder at capacity. "Something went wrong" would throw away the only part the
            // reader can do anything about.
            setProblem(failure?.response?.data?.Message || failure?.response?.data?.message
                       || "That search could not be run.");
            setHits(null);
            setMode(null);
            setNote(null);
        } finally {
            setBusy(false);
        }
    }, [query, busy, sessionContext, classificationCode]);

    return (
        <>
            <Box ref={anchor} sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                <TravelExploreOutlined sx={{fontSize: 17, color: Blue3, flex: "0 0 auto"}}/>
                <TextField value={query} size="small" variant="standard"
                           placeholder={`Search inside the files attached to ${classificationPlural.toLowerCase()}…`}
                           onChange={(e) => setQuery(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
                           // REOPEN ON FOCUS, so a reader who dismissed the panel and clicked back
                           // into the box gets their results again rather than an empty box that
                           // has apparently forgotten the question still written in it.
                           onFocus={() => { if (hits || problem) setOpen(true); }}
                           slotProps={{input: {endAdornment: (
                               <InputAdornment position="end">
                                   <Tooltip title="Search inside the attached files">
                                       <span>
                                           <IconButton size="small" onClick={run}
                                                       disabled={!query.trim() || busy}
                                                       sx={{color: Blue3}}
                                                       aria-label="search attachments">
                                               {busy ? <CircularProgress size={15} sx={{color: Blue3}}/>
                                                     : <SearchRounded sx={{fontSize: 18}}/>}
                                           </IconButton>
                                       </span>
                                   </Tooltip>
                               </InputAdornment>)}}}
                           sx={{width: 340, "& .MuiInputBase-input": {fontSize: 13}}}/>
            </Box>

            <Popover open={open && (!!hits || !!problem || busy)} anchorEl={anchor.current}
                     onClose={() => setOpen(false)}
                     anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                     transformOrigin={{vertical: "top", horizontal: "right"}}
                     slotProps={{paper: {sx: {width: 560, maxHeight: "60vh", padding: "12px 14px",
                                              border: `1px solid ${Grey2}`, borderRadius: "10px"}}}}>

                {/* WHAT WAS SEARCHED, IN THE PLACE IT QUALIFIES SOMETHING. The classification is
                    why a Purchase Order's certificate is not here; the view is why somebody else's
                    Document is not. On the page above it this said what the tab row already said;
                    attached to an answer it is the difference between trusting the answer and
                    misreading it. */}
                <Typography sx={{fontSize: 11.5, color: Grey6}}>
                    Searching the files attached to <b>{classificationPlural}</b>
                    {persona ? <> you can see as <b>{persona.type}</b>
                        {persona.name ? ` · ${persona.nameLabel}: ${persona.name}` : ""}</> : null}.
                </Typography>

                {/* HOW IT RANKED. Not redundant with anything on the page: nothing else says the
                    embedding model was unreachable, and a keyword-only answer cannot find a
                    paraphrase. Amber when it is not the full search. */}
                {mode && !problem ? (
                    <Typography sx={{fontSize: 11.5, marginTop: "3px",
                                     color: mode === "hybrid" ? Grey6 : "#8A6D00"}}>
                        {mode === "hybrid"
                            ? "Ranked by meaning and by wording."
                            : mode === "semantic"
                                ? "Ranked by meaning only — no word in that question is indexable."
                                : "Ranked by wording only — an exact-word match. A paraphrase of "
                                  + "your question would not have been found."}
                        {note ? ` ${note}` : ""}
                    </Typography>
                ) : null}

                {problem ? (
                    <Typography sx={{fontSize: 12.5, color: "#B3261E", marginTop: "8px"}}>
                        {problem}
                    </Typography>
                ) : null}

                {hits && hits.length === 0 && !problem ? (
                    <Typography sx={{fontSize: 12.5, color: Grey6, marginTop: "8px"}}>
                        {/* THE EMPTY STATE DEPENDS ON THE MODE, because "nothing addresses that"
                            is only true if meaning was searched. Ranked by wording alone, the
                            honest sentence is that those exact words are not in the files -- a
                            different and much weaker statement, and one the reader can act on by
                            rewording. */}
                        {mode === "keyword"
                            ? <>None of those files contain those words. This ranked by wording
                                only, so a document that says the same thing differently would not
                                have been found — try the terms you expect to appear in the file
                                itself.</>
                            : <>Nothing in those files addresses that. This searches the TEXT
                                INSIDE attached files — the filters on the left search a record's
                                own name, description and form answers, which is a different
                                question.</>}
                    </Typography>
                ) : null}

                {hits && hits.length > 0 ? (
                    <Box sx={{marginTop: "10px", display: "flex", flexDirection: "column",
                              gap: "8px"}}>
                        {hits.map(hit => (
                            <Box key={hit.chunkUSID}
                                 onClick={() => { setOpen(false);
                                                  navigate(`/object/${hit.objectRevisionUSID}`); }}
                                 sx={{cursor: "pointer", padding: "8px 10px", borderRadius: "8px",
                                      border: `1px solid ${Grey2}`,
                                      "&:hover": {backgroundColor: "#F7FAFC"}}}>
                                <Box sx={{display: "flex", alignItems: "baseline", gap: 1}}>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, color: Blue3}}>
                                        {hit.objectID || "(record)"}
                                    </Typography>
                                    <Typography sx={{fontSize: 12.5, color: Grey17, flex: 1,
                                                     overflow: "hidden", textOverflow: "ellipsis",
                                                     whiteSpace: "nowrap"}}>
                                        {hit.recordName}
                                    </Typography>
                                    <Typography sx={{fontSize: 11, color: Grey6}}>
                                        {hit.fileName}{hit.page ? ` · page ${hit.page}` : ""}
                                    </Typography>
                                    {/* WHICH ARM FOUND IT, only when both ran -- with one arm it
                                        would label every row identically. */}
                                    {mode === "hybrid" && hit.foundBy.length > 0 ? (
                                        <Typography sx={{fontSize: 10, color: Grey6,
                                                         border: `1px solid ${Grey2}`,
                                                         borderRadius: "4px", padding: "0 5px",
                                                         whiteSpace: "nowrap"}}>
                                            {hit.foundBy.join(" + ")}
                                        </Typography>
                                    ) : null}
                                </Box>
                                {/* THE PASSAGE, not a summary of it. What matched is the thing
                                    worth reading, and it is what somebody checks against the
                                    document. */}
                                <Typography sx={{fontSize: 12.5, color: Grey17, marginTop: "4px",
                                                 fontStyle: "italic"}}>
                                    “{hit.text.length > 240 ? `${hit.text.slice(0, 240)}…` : hit.text}”
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : null}
            </Popover>
        </>
    );
}
